import { API, graphqlOperation } from 'aws-amplify';

import { type QueryResult } from '../../../Admin/Datasources/QueryFactory';
import { reservationItemWithoutResourcesFields } from '../../../Admin/Scheduling/Datasources/constants';
import { reservationDataToReservation } from '../../../Admin/Scheduling/Datasources/reservationDataToReservation';
import { type Reservation } from '../../../Admin/Scheduling/model/SchedulingTypes';
import { reservationDataFromDb } from '../../../Admin/Scheduling/model/SchedulingTypes';
import { type CreateReservationInput } from '../../../API';
import { type LocalDB } from '../../LocalDB/LocalDB';
import { type DbCreated, type DbItems, type MaybeNextToken } from '../../LocalDB/types';
import { type FlightSupplementalReservation } from '../FlightSupplementalDataTypes';
import { FlightSupplementalDataType } from '../FlightSupplementalDataTypes';
import { itemIsUseful } from './Cleanup';
import { findContactInReservation } from './FindContactInReservation';

const DAY = 86400 * 1e3;
const RESERVATIONS_SYNC_PAGE_SIZE = 200;
const GET_RESERVATIONS_BATCH_SIZE = 25;
// reservation objects are huge - the full sync loads only those ending within this horizon
const FULL_SYNC_HORIZON_MS = 14 * DAY;
// OPS writes the Reservation before its ReservationResource rows, so a delta cursor taken in between would skip
// the reservation for good (and device clocks drift) - re-scan a short overlap; re-fetching is idempotent.
const DELTA_OVERLAP_MS = 5 * 60 * 1e3;

export type ReservationsSyncOptions = {
    doFullResync: boolean;
    lastSync: number;
};

export async function loadReservations(contactId: string, db: LocalDB, options: ReservationsSyncOptions) {
    if (options.doFullResync) {
        await loadReservationsFull(contactId, db);
    } else {
        await loadReservationsDelta(contactId, db, options.lastSync);
    }
}

async function loadReservationsFull(contactId: string, db: LocalDB) {
    const now = Date.now();
    const reservationDateTo = {
        between: [new Date(now - DAY).toISOString(), new Date(now + FULL_SYNC_HORIZON_MS).toISOString()],
    };
    const idsToDelete: string[] = [];
    const reservations: Reservation[] = [];
    let nextToken: string | null | undefined;

    do {
        const result = (await API.graphql(
            graphqlOperation(reservationsQuery, { contactId, reservationDateTo, nextToken })
        )) as QueryResult<ReservationsReturnType>;
        if (result.errors) {
            console.error(result.errors);
            throw new Error('Failed to load reservations (full resync)');
        }
        const { items, nextToken: newNextToken } = result.data.listReservationResourcesByContactId;
        for (const item of items) {
            const reservation = classifyReservation(item.reservation, contactId, idsToDelete);
            if (reservation) {
                reservations.push(reservation);
            }
        }
        nextToken = newNextToken;
    } while (nextToken);

    await persistReservations(db, reservations, idsToDelete);
}

// The delta query scans the whole (fleet-wide) Reservation Delta table, not just this contact's
// reservations - AppSync's syncReservations has no per-contact index. Keeping the selection set lean
// (id/dateTo/deleted/resources only, no reservationData/paxData) means the irrelevant items we scan
// past and discard here are cheap; the expensive fields are only fetched, per-id, in the second pass
// below, for reservations that actually concern this contact.
async function loadReservationsDelta(contactId: string, db: LocalDB, lastSyncMs: number) {
    const filter = { dateTo: { gt: new Date(Date.now() - DAY).toISOString() } };
    const idsToFetch: string[] = [];
    const idsToDelete: string[] = [];
    let nextToken: string | null | undefined;

    do {
        const result = (await API.graphql(
            graphqlOperation(reservationsSyncQuery, {
                lastSync: lastSyncMs - DELTA_OVERLAP_MS,
                filter,
                limit: RESERVATIONS_SYNC_PAGE_SIZE,
                nextToken,
            })
        )) as QueryResult<ReservationsSyncReturnType>;
        if (result.errors) {
            console.error(result.errors);
            throw new Error('Failed to sync reservations (delta)');
        }
        const { items, nextToken: newNextToken } = result.data.syncReservations;
        for (const item of items) {
            const myResource = item.resources?.items.find((resource) => resource.contactId === contactId);
            if (!myResource) {
                continue; // never concerned this contact
            }
            if (item.deleted || myResource.deleted) {
                idsToDelete.push(item.id);
            } else {
                idsToFetch.push(item.id);
            }
        }
        nextToken = newNextToken;
    } while (nextToken);

    const reservations: Reservation[] = [];
    for (let i = 0; i < idsToFetch.length; i += GET_RESERVATIONS_BATCH_SIZE) {
        const batchIds = idsToFetch.slice(i, i + GET_RESERVATIONS_BATCH_SIZE);
        const batchResult = (await API.graphql(graphqlOperation(buildGetReservationsQuery(batchIds)))) as QueryResult<
            Record<string, (CreateReservationInput & DbCreated) | null>
        >;
        if (batchResult.errors) {
            console.error(batchResult.errors);
            throw new Error('Failed to fetch changed reservations');
        }
        for (const input of Object.values(batchResult.data)) {
            if (!input) {
                continue;
            }
            const reservation = classifyReservation(input, contactId, idsToDelete);
            if (reservation) {
                reservations.push(reservation);
            }
        }
    }

    await persistReservations(db, reservations, idsToDelete);
}

function classifyReservation(
    input: CreateReservationInput & DbCreated,
    contactId: string,
    idsToDelete: string[]
): Reservation | null {
    const reservationData = reservationDataFromDb({ input, isQuotation: false });
    if (!reservationData) {
        return null;
    }
    const reservation = reservationDataToReservation(reservationData);
    const contactIsInReservation = findContactInReservation(reservation, contactId);
    const usefulnessEnd = getReservationUsefulnessEndDate(reservation);
    const isUseless = usefulnessEnd.getTime() < Date.now();
    if (reservationData.deleted || reservation.deleted || !contactIsInReservation || isUseless) {
        // if user was removed from reservation, consider as if reservation was deleted
        idsToDelete.push(reservation.id);

        return null;
    }

    return reservation;
}

async function persistReservations(db: LocalDB, reservations: Reservation[], idsToDelete: string[]) {
    const table = db.flightSupplementalData;
    const supplementalData = reservations.map(reservationToSupplementalData).filter(itemIsUseful);
    await table.bulkPut(supplementalData);
    await table.bulkDelete(idsToDelete);
}

function buildGetReservationsQuery(ids: string[]): string {
    const fields = ids
        .map((id, i) => `r${i}: getReservation(id: ${JSON.stringify(id)}) { ${reservationItemWithoutResourcesFields} }`)
        .join('\n');

    return `query getChangedReservations {\n${fields}\n}`;
}

const reservationsQuery = `
query updateSupplementalData($contactId: ID!, $reservationDateTo: ModelStringKeyConditionInput, $nextToken: String) {
    listReservationResourcesByContactId: listReservationResourcesByContactIdSortByDate(contactId: $contactId, sortDirection: ASC, limit: 100, reservationDateTo: $reservationDateTo, nextToken: $nextToken) {
        items {
            reservation {
                ${reservationItemWithoutResourcesFields}
            }
        }
        nextToken
    }
}`;

const reservationsSyncQuery = `
query syncSupplementalReservations($lastSync: AWSTimestamp, $filter: ModelReservationFilterInput, $limit: Int, $nextToken: String) {
    syncReservations(lastSync: $lastSync, filter: $filter, limit: $limit, nextToken: $nextToken) {
        items {
            id
            dateTo
            deleted
            resources {
                items {
                    contactId
                    deleted
                }
            }
        }
        nextToken
    }
}`;

type ReservationsReturnType = {
    listReservationResourcesByContactId: DbItems<{ reservation: CreateReservationInput & DbCreated }> & MaybeNextToken;
};

type ReservationSyncItem = {
    id: string;
    dateTo: string;
    deleted: boolean;
    resources: { items: Array<{ contactId: string | null; deleted: boolean | null }> } | null;
};

type ReservationsSyncReturnType = {
    syncReservations: MaybeNextToken & {
        items: ReservationSyncItem[];
    };
};

function getReservationUsefulnessEndDate(reservation: Reservation): Date {
    return new Date(reservation.dateTo.getTime() + 2 * DAY);
}

function reservationToSupplementalData(reservation: Reservation): FlightSupplementalReservation {
    return {
        id: reservation.id,
        reservationId: reservation.id,
        usefulnessEndsByDate: getReservationUsefulnessEndDate(reservation),
        type: FlightSupplementalDataType.reservation,
        reservation,
    };
}
